import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, Clock, Lock, ArrowRight } from 'lucide-react';
import { GOLD, NAVY, alpha } from '../theme/palette';
import { useTourTracking } from '../utils/analytics';

// Belmond Cathedral theme tokens
const SERIF = { fontFamily: '"Fraunces", serif' };
const SANS = { fontFamily: '"Inter", sans-serif' };

const TourCard = ({ tour }) => {
  const trackProps = useTourTracking(tour.id);
  const navigate = useNavigate();
  const { language } = useLanguage();

  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const labels = {
    pt: { details: 'Ver Detalhes', book: 'Reservar', from: 'Preço', waitlist: 'Lista de Espera', privateView: 'Ver Detalhes' },
    en: { details: 'View Details', book: 'Book Now', from: 'Price', waitlist: 'Join Waitlist', privateView: 'View Details' },
    es: { details: 'Ver Detalles', book: 'Reservar', from: 'Precio', waitlist: 'Lista de Espera', privateView: 'Ver Detalles' },
  };

  const tagLabels = {
    esgotado: { pt: 'Esgotado', en: 'Sold Out', es: 'Agotado' },
    ultimas_vagas: { pt: 'Últimas Vagas', en: 'Last Spots', es: 'Últimas Plazas' },
    em_breve: { pt: 'Em Breve', en: 'Coming Soon', es: 'Próximamente' },
    concluida: { pt: 'Já Realizada', en: 'Concluded', es: 'Realizada' },
  };

  // Iter 320br — Past trips take precedence over other status tags
  const statusTag = tour.is_past ? 'concluida' : tour.status_tag;
  const isPrivateGated = tour.visibility === 'private_gated';
  const isSoldOut = statusTag === 'esgotado';

  const cardImgSrc = tour.image_card || tour.image;
  const resolvedImage = cardImgSrc?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${cardImgSrc}` : cardImgSrc;
  const dateFmt = language === 'pt' ? 'pt-BR' : language === 'es' ? 'es-ES' : 'en-US';
  const dayMonth = (d) => new Date(d + 'T12:00:00').toLocaleDateString(dateFmt, { day: '2-digit', month: 'short' });

  return (
    <article
      className="group cursor-pointer flex flex-col rounded-2xl p-3 sm:p-4 transition-shadow hover:shadow-md"
      style={{
        ...SANS,
        // Iter 358b — outline only, no fill. White background keeps the card
        // airy; the subtle gold-tinted border frames each trip without
        // competing with the hero image.
        background: '#ffffff',
        border: '1px solid color-mix(in srgb, var(--site-gold) 28%, transparent)'
      }}
      onClick={() => navigate(`/tour/${tour.id}`)}
      {...trackProps}
      data-testid={`tour-card-${tour.id}`}
    >
      {/* Iter 357 — Landscape 4:3 card image. Previously 4:5 portrait, but 
          on compact desktops (1366×768) the taller image pushed title/price 
          below the fold and users had to scroll just to see trip info. 
          Recommended generation size: 1600×1200 px. */}
      <div
        className="relative overflow-hidden mb-5 rounded-xl"
        style={{ aspectRatio: '4 / 3' }}
        data-testid={`tour-card-image-link-${tour.id}`}
      >
        <img
          src={resolvedImage}
          alt={tour.name[language]}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          style={{ objectPosition: 'center center' }}
          data-testid={`tour-card-image-${tour.id}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/15" />

        {/* Duration badge · top-left */}
        <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase px-2.5 py-1 font-medium" style={{ background: GOLD, color: NAVY }}>
          <Clock className="w-3 h-3" />
          {tour.duration} {language === 'pt' ? 'dias' : language === 'en' ? 'days' : 'días'}
        </div>

        {/* Status badge · top-right (or private-gated badge) */}
        {statusTag && tagLabels[statusTag] && (
          <div className="absolute top-4 right-4 text-[10px] tracking-[0.3em] uppercase px-2.5 py-1 font-medium"
            style={{
              background: statusTag === 'esgotado' ? '#8c1d1d' : statusTag === 'ultimas_vagas' ? '#b85c00' : statusTag === 'em_breve' ? '#2d5f3f' : '#4a4a4a',
              color: '#fff',
            }}
            data-testid={`tour-badge-status-${tour.id}`}
          >
            {tagLabels[statusTag][language]}
          </div>
        )}
        {!statusTag && isPrivateGated && (
          <div className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] tracking-[0.3em] uppercase px-2.5 py-1 font-medium backdrop-blur-sm" style={{ background: 'color-mix(in srgb, var(--site-navy) 78%, transparent)', color: '#fff' }} data-testid={`tour-badge-private-${tour.id}`}>
            <Lock className="w-3 h-3" />
            {(tour[`group_label_${language}`] || tour.group_label_pt) || (language === 'pt' ? 'Grupo Exclusivo' : language === 'en' ? 'Exclusive Group' : 'Grupo Exclusivo')}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col">
        <h3 style={SERIF} className="text-2xl lg:text-3xl font-light leading-tight mb-2" data-testid={`tour-card-name-${tour.id}`}>
          {tour.name[language]}
        </h3>

        {(tour.departure_date || tour.arrival_date) && (
          <div className="flex items-center gap-1.5 text-xs opacity-70 mb-1" style={{ color: NAVY }}>
            <Calendar className="w-3.5 h-3.5" style={{ color: GOLD }} />
            <span>
              {tour.departure_date && dayMonth(tour.departure_date)}
              {tour.departure_date && tour.arrival_date && ' — '}
              {tour.arrival_date && new Date(tour.arrival_date + 'T12:00:00').toLocaleDateString(dateFmt, { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>
        )}

        <p className="text-sm leading-relaxed mb-2 line-clamp-2 opacity-70 break-words whitespace-pre-line" style={{ color: NAVY }} data-testid={`tour-card-desc-${tour.id}`}>
          {(() => {
            // Iter 358g — Preserve admin newlines (textarea Enter → literal \n)
            // so the card teaser reflects the admin's line breaks. We still
            // strip HTML tags (DOMParser.textContent) and collapse HORIZONTAL
            // whitespace runs, but keep '\n' intact. `whitespace-pre-line`
            // CSS makes '\n' render as a line break while allowing normal
            // word-wrap. `line-clamp-2` still caps at 2 visible lines.
            const raw = tour[`card_teaser_${language}`]
              || tour.card_teaser_pt
              || stripHtml(tour.description[language] || '').slice(0, 180);
            return stripHtml(String(raw))
              .replace(/[ \t\f\v]+/g, ' ')     // collapse spaces only, keep \n
              .replace(/\n{3,}/g, '\n\n')      // cap triple+ blank lines
              .trim();
          })()}
        </p>

        {/* Price block */}
        <div className="flex items-end justify-between mt-auto pt-2.5 border-t" style={{ borderColor: `${alpha(GOLD, '44')}` }}>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-0.5">{labels[language].from}</div>
            <div className="text-3xl lg:text-4xl font-bold leading-none" style={SANS}>
              <span className="text-lg align-top opacity-70">{tour.currency === 'EUR' ? '€' : 'US$ '}</span>
              <span data-testid={`tour-card-price-${tour.id}`}>{tour.price?.toLocaleString('en-US')}</span>
            </div>
            {tour.land_only_enabled && tour.air_fare > 0 && tour.price > tour.air_fare && (
              <div className="text-[10px] tracking-[0.2em] uppercase mt-1 opacity-60" data-testid={`tour-card-land-only-${tour.id}`}>
                {language === 'pt' ? 'Terrestre' : language === 'en' ? 'Land only' : 'Sólo terrestre'}: <span className="font-bold">{tour.currency === 'EUR' ? '€' : '$'}{(tour.price - tour.air_fare).toLocaleString('en-US')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2 mt-5">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/tour/${tour.id}`); }}
            className={`${tour.is_past ? 'w-full' : 'flex-1'} inline-flex items-center justify-center gap-2 py-3 px-4 text-[10px] tracking-[0.3em] uppercase font-semibold border-2 transition hover:opacity-90`}
            style={{ borderColor: NAVY, color: NAVY }}
            data-testid={`tour-details-btn-${tour.id}`}
          >
            {labels[language].details} <ArrowRight className="w-3.5 h-3.5" />
          </button>
          {!tour.is_past && isSoldOut ? (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/tour/${tour.id}?waitlist=true`); }}
              className="flex-1 py-3 px-4 text-[10px] tracking-[0.3em] uppercase font-semibold transition hover:opacity-90"
              style={{ background: '#b85c00', color: '#fff' }}
              data-testid={`tour-waitlist-btn-${tour.id}`}
            >
              {labels[language].waitlist}
            </button>
          ) : !tour.is_past && isPrivateGated ? (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/tour/${tour.id}`); }}
              className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 text-[10px] tracking-[0.3em] uppercase font-semibold transition hover:opacity-90"
              style={{ background: NAVY, color: GOLD }}
              data-testid={`tour-private-btn-${tour.id}`}
            >
              <Lock className="w-3 h-3" /> {labels[language].privateView}
            </button>
          ) : !tour.is_past && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/booking/${tour.id}`); }}
              className="flex-1 py-3 px-4 text-[10px] tracking-[0.3em] uppercase font-semibold transition hover:opacity-90"
              style={{ background: GOLD, color: NAVY }}
              data-testid={`tour-book-btn-${tour.id}`}
            >
              {labels[language].book}
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default TourCard;
